import { useEffect, useState } from "preact/hooks";
import type { LiveSheetItem } from "../../types/generated/live";
import { Icon, type IconName } from "../ui/icon";
import { useMenuKeys } from "../ui/menu_keys";
import { PopoverPanel, usePopover } from "../ui/popover";
import { ACTION_ROW, Button, TextButton } from "./controls";
import type { Mover } from "./mover";
import type { InventoryEdits } from "./edits";
import { interpolate, type Labels } from "./labels";
import type { Popup } from "./popups";

// The row an item leaves takes the focus with it, so it goes to the grip of the next row, the previous one, or the add button.
function focusNeighbour(trigger: HTMLElement | null) {
	const row = trigger?.closest<HTMLElement>("[data-sortable-row]");
	const rows = [...(row?.closest("tbody")?.querySelectorAll<HTMLElement>("[data-sortable-row]") ?? [])];
	const at = row ? rows.indexOf(row) : -1;
	const target = (rows[at + 1] ?? rows[at - 1])?.querySelector<HTMLElement>("[data-grip]") ?? document.querySelector<HTMLElement>("[data-inventory-add]");
	requestAnimationFrame(() => target?.focus());
}

const CHOICE = "h-9 rounded-lg border border-line bg-surface-raised px-2 text-base text-ink";

const ITEM = "flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold hover:bg-surface-sunken";

function Entry({ id, icon, label, danger = false, onClick }: { id?: string; icon: IconName; label: string; danger?: boolean; onClick: () => void }) {
	return (
		<button type="button" role="menuitem" data-entry={id} class={`${ITEM} text-ink`} onClick={onClick}>
			<Icon name={icon} class={`h-4 w-4 ${danger ? "text-carmesim-600" : "text-ink-muted"}`} />
			{label}
		</button>
	);
}

// An item's ⋯ menu: Editar opens the form, the move (only where the item has somewhere to go) and Excluir each ask inside
// the menu, never in a browser dialog.
export function ItemMenu({
	item,
	labels,
	mover,
	edits,
	open,
	notify,
}: {
	item: LiveSheetItem;
	labels: Labels;
	mover: Mover | null;
	edits: InventoryEdits;
	open: (popup: Popup, opener: HTMLElement | null) => void;
	notify: (message: string) => void;
}) {
	const popover = usePopover<HTMLButtonElement>();
	const [view, setView] = useState<"menu" | "move" | "delete">("menu");
	const [remove, setRemove] = useState(true);
	const [choice, setChoice] = useState("");
	const name = { name: item.name };
	useMenuKeys(popover, view === "menu");

	useEffect(() => {
		if (!popover.open) setView("menu");
	}, [popover.open]);
	useEffect(() => {
		if (view !== "menu") popover.panel.current?.querySelector<HTMLElement>("button, input")?.focus();
	}, [view, popover.panel]);

	// Back from a confirmation, the focus returns to the entry that asked it.
	const back = (from: "move" | "delete") => {
		setView("menu");
		requestAnimationFrame(() => popover.panel.current?.querySelector<HTMLElement>(`[data-entry="${from}"]`)?.focus());
	};
	const move = () => {
		if (remove) focusNeighbour(popover.trigger.current);
		else requestAnimationFrame(() => popover.trigger.current?.focus());
		popover.close();
		void mover?.run(choice || mover.choices[0]?.value || "", remove).then((taken) => {
			if (taken) notify(remove ? mover.moved : mover.copied);
		});
	};
	const label = interpolate(labels.inventory.actions, name);

	return (
		<>
			<button
				ref={popover.trigger}
				type="button"
				class="cs-tap inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-sunken hover:text-ink"
				aria-label={label}
				aria-haspopup="menu"
				aria-expanded={popover.open}
				aria-controls={popover.open ? popover.id : undefined}
				onClick={popover.toggle}
			>
				<Icon name="ellipsis" class="h-[18px] w-[18px]" />
			</button>
			{popover.open && (
				<PopoverPanel popover={popover} label={label} class="w-64 overflow-hidden py-1">
					{view === "menu" && (
						<div role="menu" aria-label={label}>
							<Entry
								icon="pen"
								label={labels.item.menu_edit}
								onClick={() => {
									popover.close();
									open({ kind: "item_edit", itemId: item.id }, popover.trigger.current);
								}}
							/>
							{mover && (
								<Entry
									id="move"
									icon="team"
									label={mover.entry}
									onClick={() => {
										// Each time the form opens it starts as the mover says, whatever an earlier open ticked.
										setRemove(mover.remove);
										setView("move");
									}}
								/>
							)}
							<Entry id="delete" icon="trash" label={labels.item.menu_delete} danger onClick={() => setView("delete")} />
						</div>
					)}
					{view === "move" && mover && (
						<div class="flex flex-col gap-2 p-3 text-sm">
							<p>{mover.hint}</p>
							{mover.choices.length > 0 && (
								<label class="flex flex-col gap-1 font-bold">
									{mover.choose}
									<select class={CHOICE} value={choice || mover.choices[0]?.value} onChange={(event) => setChoice(event.currentTarget.value)}>
										{mover.choices.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</label>
							)}
							<label class="flex items-center gap-2 font-bold">
								<input
									type="checkbox"
									class="h-[18px] w-[18px] accent-carmesim-600"
									checked={remove}
									onChange={(event) => setRemove(event.currentTarget.checked)}
								/>
								{labels.item.move_remove}
							</label>
							<div class={ACTION_ROW}>
								<Button tone="primary" icon="team" label={remove ? mover.move : mover.copy} onClick={move} />
								<TextButton label={labels.edit.cancel} onClick={() => back("move")} />
							</div>
						</div>
					)}
					{view === "delete" && (
						<div class="flex flex-col gap-2 p-3 text-sm">
							<p>{interpolate(labels.item.delete_confirm, name)}</p>
							<div class={ACTION_ROW}>
								<Button
									tone="primary"
									icon="trash"
									label={labels.item.menu_delete}
									onClick={() => {
										focusNeighbour(popover.trigger.current);
										edits.destroyItem(item.id);
									}}
								/>
								<TextButton label={labels.edit.cancel} onClick={() => back("delete")} />
							</div>
						</div>
					)}
				</PopoverPanel>
			)}
		</>
	);
}
